import type {
	InvestmentMinInfo,
	InvestmentProjectedListEntry,
	InvestmentReferenceListEntry,
	InvestmentStatus,
	InvestmentSummary,
	MarketViewListEntry,
	ReferenceUniverseListEntry,
} from "@eu.mdotm.api/sphere";
import type { RichAcl } from "@eu.mdotm.api/smart-workspaces";
import { platformToast } from "$root/notification-system/toast";
import { parallelize } from "$root/utils";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { match } from "ts-pattern";
import type { PortfolioStudioTab } from "../portfolio-studio-tabs";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";

type MultipleEntityDelete =
	| {
			area: "Portfolios";
			items: Array<InvestmentSummary | InvestmentProjectedListEntry>;
	  }
	| {
			area: "References";
			items: Array<ReferenceItem>;
	  }
	| {
			area: "Universes";
			items: Array<ReferenceUniverseListEntry>;
	  }
	| {
			area: "MarketViews";
			items: Array<MarketViewListEntry>;
	  };

const getDeleteApiByArea = () => ({
	Portfolios: portfolioRepository.delete,
	Universes: universeRepository.delete,
	MarketViews: marketViewRepository.delete,
	References: {
		References: referenceRepository.deleteReference,
		Benchmark: referenceRepository.deleteReference,
		LegacyBenchmark: referenceRepository.deleteBenchmark,
	},
});

function errorToast(message: string) {
	platformToast({
		children: message,
		severity: "error",
		icon: "Portfolio",
	});
}

type ResponseStack = { name?: string };

export function notifyUser(params: {
	succeded: Array<ResponseStack>;
	area: PortfolioStudioTab;
	t: (...params: any) => string;
}): void {
	if (params.succeded.length > 1) {
		const translationKey = match(params)
			.with({ area: "Portfolios" }, () => "PUSH_NOTIFICATION.MULTI_PORTFOLIO_DELETED_TITLE_SUCCESS")
			.with({ area: "References" }, () => "PUSH_NOTIFICATION.MULTI_REFERENCES_DELETED_TITLE_SUCCESS")
			.with({ area: "Universes" }, () => "PUSH_NOTIFICATION.MULTI_UNIVERSE_DELETED_TITLE_SUCCESS")
			.with({ area: "MarketViews" }, () => "PUSH_NOTIFICATION.MULTI_MARKET_DELETED_TITLE_SUCCESS")
			.exhaustive();

		platformToast({
			children: params.t(translationKey, { count: params.succeded.length }),
			severity: "success",
			icon: params.area === "MarketViews" ? "News-category-Market-view-aligment" : "Portfolio",
		});
	}

	if (params.succeded.length === 1) {
		const deletedItem = params.succeded[0];

		const feedback = match(params)
			.with({ area: "Portfolios" }, () => `Successfully deleted your portfolio “${deletedItem.name ?? "-"}”`)
			.with({ area: "References" }, () => `Successfully deleted your reference “${deletedItem.name ?? "-"}”`)
			.with({ area: "Universes" }, () => `Successfully deleted your universe “${deletedItem.name ?? "-"}”`)
			.with({ area: "MarketViews" }, () => `Successfully deleted your market view “${deletedItem.name ?? "-"}”`)
			.exhaustive();

		platformToast({
			children: feedback,
			severity: "success",
			icon: params.area === "MarketViews" ? "News-category-Market-view-aligment" : "Portfolio",
		});
	}
}

export async function multipleEntityDelete(
	params: MultipleEntityDelete,
	showToastForEachFailed = true,
): Promise<{
	succeded: ResponseStack[];
	failed: ResponseStack[];
}> {
	const succeded: Array<ResponseStack> = [];
	const failed: Array<ResponseStack> = [];
	const deleteApiByArea = getDeleteApiByArea();
	const stack = match(params)
		.with({ area: "Portfolios" }, ({ items, area }) => {
			return items.map(
				({ uuid, name }) =>
					() =>
						deleteApiByArea[area](uuid!)
							.then(() => succeded.push({ name }))
							.catch(() => {
								if (showToastForEachFailed) {
									errorToast(`Failed to delete your portfolio “${name ?? "-"}”`);
								}
								failed.push({ name });
							}),
			);
		})
		.with({ area: "References" }, ({ area, items }) => {
			return items.map(
				({ name, uuid: identifier, type }) =>
					() =>
						deleteApiByArea[area]
							[type](identifier!)
							.then(() => succeded.push({ name }))
							.catch(() => {
								if (showToastForEachFailed) {
									errorToast(
										type === "Benchmark"
											? `Failed to delete your benchmark “${name ?? "-"}”`
											: `Failed to delete your portfolio target “${name ?? "-"}”`,
									);
								}
								failed.push({ name });
							}),
			);
		})
		.with({ area: "MarketViews" }, ({ area, items }) => {
			return items.map(
				({ uuid, name }) =>
					() =>
						deleteApiByArea[area](uuid!)
							.then(() => succeded.push({ name }))
							.catch(() => {
								if (showToastForEachFailed) {
									errorToast(`Failed to delete your market view “${name ?? "-"}”`);
								}
								failed.push({ name });
							}),
			);
		})
		.with({ area: "Universes" }, ({ area, items }) => {
			return items.map(
				({ uuid, name }) =>
					() =>
						deleteApiByArea[area](uuid!)
							.then(() => succeded.push({ name }))
							.catch(() => {
								if (showToastForEachFailed) {
									errorToast(`Failed to delete your universe “${name ?? "-"}”`);
								}
								failed.push({ name });
							}),
			);
		})
		.exhaustive();

	await parallelize(stack, { concurrency: 32 });
	return { succeded, failed };
}

export type ReferenceItem = {
	name?: string;
	uuid?: string;
	creationTime?: string;
	modificationTime?: string;
	status?: InvestmentStatus;
	type: "References" | "Benchmark" | "LegacyBenchmark";
	linkedPortfolio?: InvestmentMinInfo[];
	richAcl?: RichAcl;
	nofPortfolios?: number;
};

export function createReferenceRows(
	benchmarks?: InvestmentReferenceListEntry[],
	references?: InvestmentReferenceListEntry[],
): Array<ReferenceItem> {
	const mapReferences = (references ?? []).map(
		(r): ReferenceItem => ({
			name: r.name,
			uuid: r.uuid,
			creationTime: r.creationTime,
			modificationTime: r.modificationTime,
			status: r.status,
			type: r.benchmarkLabeled ? "Benchmark" : "References",
			linkedPortfolio: r.referralInvestments,
			richAcl: r.richAcl,
			nofPortfolios: r.nofPortfolios,
		}),
	);

	const mapBenchmarks = (benchmarks ?? []).map(
		(b): ReferenceItem => ({
			name: b.name,
			uuid: b.uuid,
			creationTime: b.creationTime,
			modificationTime: b.modificationTime,
			status: b.status,
			type: b.legacyBenchmark ? "LegacyBenchmark" : "Benchmark",
			linkedPortfolio: b.referralInvestments,
			richAcl: b.richAcl,
			nofPortfolios: b.nofPortfolios,
		}),
	);

	return [...mapReferences, ...mapBenchmarks].sort(builtInSortFnFor("modificationTime"));
}
